import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, X, ShieldCheck, Clock, Pause, Play, Trash2 } from 'lucide-react';
import { tabService, type BlockRule } from '@/services/tabs';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { toast } from 'sonner';

interface BlockRulesPanelProps {
  compact?: boolean;
  onActiveSitesChange?: (sites: string[]) => void;
}

export function BlockRulesPanel({ compact = false, onActiveSitesChange }: BlockRulesPanelProps) {
  const [rules, setRules] = useState<BlockRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewRule, setShowNewRule] = useState(false);
  const [newSiteInput, setNewSiteInput] = useState<Record<string, string>>({});
  const [newRule, setNewRule] = useState({ name: '', site: '' });

  const cleanDomain = (raw: string): string => {
    let d = raw.trim().toLowerCase();
    d = d.replace(/^https?:\/\//i, '');
    d = d.replace(/^www\./i, '');
    d = d.split('/')[0];
    d = d.split('?')[0];
    d = d.split('#')[0];
    d = d.split(':')[0]; // strip port if any
    return d.trim();
  };

  const activeSites = useMemo(
    () => rules.filter((r) => r.isActive).flatMap((r) => r.sites),
    [rules],
  );

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tabService.getBlockRules();
      setRules(data);
    } catch {
      toast.error('Failed to load block rules');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    onActiveSitesChange?.(activeSites);
  }, [activeSites, onActiveSitesChange]);

  const toggleRule = async (id: string) => {
    try {
      await tabService.toggleBlockRule(id);
      setRules((prev) =>
        prev.map((r) => (r.id === id ? { ...r, isActive: !r.isActive } : r)),
      );
      toast.success('Rule updated');
    } catch {
      toast.error('Failed to update rule');
    }
  };

  const deleteRule = async (id: string) => {
    try {
      await tabService.deleteBlockRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
      toast.success('Site removed from block list');
    } catch {
      toast.error('Failed to remove rule');
    }
  };

  const addSiteToGroup = async (groupName: string, sampleRule?: BlockRule) => {
    const rawInput = newSiteInput[groupName] ?? '';
    const site = cleanDomain(rawInput);
    if (!site) {
      toast.error('Enter a valid domain (e.g. youtube.com)');
      return;
    }
    try {
      await tabService.createBlockRule({
        name: groupName,
        sites: [site],
        schedule: sampleRule?.schedule || 'Always',
        isActive: true,
      });
      setNewSiteInput((prev) => ({ ...prev, [groupName]: '' }));
      await fetchRules();
      toast.success(`${site} added to ${groupName}`);
    } catch {
      toast.error('Failed to add site');
    }
  };

  const handleCreateRule = async () => {
    const site = cleanDomain(newRule.site);
    if (!site) {
      toast.error('Enter a valid domain to block (e.g. tiktok.com)');
      return;
    }
    const ruleName = newRule.name.trim() || 'Custom Block';
    try {
      await tabService.createBlockRule({
        name: ruleName,
        sites: [site],
        schedule: 'Always',
        isActive: true,
      });
      setShowNewRule(false);
      setNewRule({ name: '', site: '' });
      await fetchRules();
      toast.success(`Block rule created for ${site}`);
    } catch {
      toast.error('Failed to create rule');
    }
  };

  const groupedRules = useMemo(() => {
    const groups = new Map<string, BlockRule[]>();
    for (const rule of rules) {
      const key = rule.name || 'Custom';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(rule);
    }
    return Array.from(groups.entries());
  }, [rules]);

  const blockedToday = rules.reduce((sum, r) => sum + r.blockedToday, 0);

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!compact ? (
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2 text-center">
            <p className="text-lg font-bold text-emerald-300">{blockedToday}</p>
            <p className="text-[10px] text-khiimori-text-muted">Blocked today</p>
          </div>
          <div className="rounded-lg bg-primary-500/10 border border-primary-500/20 p-2 text-center">
            <p className="text-lg font-bold text-primary-300">
              {rules.filter((r) => r.isActive).length}
            </p>
            <p className="text-[10px] text-khiimori-text-muted">Active rules</p>
          </div>
          <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-2 text-center">
            <p className="text-lg font-bold text-purple-300">{activeSites.length}</p>
            <p className="text-[10px] text-khiimori-text-muted">Sites blocked</p>
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-khiimori-text-secondary flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          Block rules
        </span>
        <button
          type="button"
          onClick={() => setShowNewRule(true)}
          className="flex items-center gap-1 text-xs font-medium text-primary-400 hover:text-primary-300"
        >
          <Plus className="w-3.5 h-3.5" />
          New rule
        </button>
      </div>

      {groupedRules.length === 0 ? (
        <p className="text-sm text-khiimori-text-muted py-2">
          No block rules yet. Add sites to block during focus sessions.
        </p>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {groupedRules.map(([groupName, groupRules]) => (
            <div
              key={groupName}
              className="rounded-lg border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-khiimori-text-primary">{groupName}</span>
                <span className="text-[10px] text-khiimori-text-muted flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Always
                </span>
              </div>
              <div className="space-y-2">
                {groupRules.map((rule) => (
                  <div key={rule.id} className="flex items-center gap-2 flex-wrap">
                    {rule.sites.map((site) => (
                      <span
                        key={`${rule.id}-${site}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-500/15 text-red-300 border border-red-500/25"
                      >
                        {site}
                        <button
                          type="button"
                          onClick={() => void deleteRule(rule.id)}
                          className="hover:text-red-200"
                          aria-label={`Remove ${site}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={() => void toggleRule(rule.id)}
                      className={`ml-auto flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        rule.isActive
                          ? 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
                          : 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25'
                      }`}
                    >
                      {rule.isActive ? (
                        <>
                          <Pause className="w-3 h-3" /> Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-3 h-3" /> Activate
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void deleteRule(rule.id)}
                      className="p-1 rounded text-khiimori-text-muted hover:text-red-400"
                      aria-label="Delete rule"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Add site (e.g. youtube.com)"
                  value={newSiteInput[groupName] ?? ''}
                  onChange={(e) =>
                    setNewSiteInput((prev) => ({
                      ...prev,
                      [groupName]: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void addSiteToGroup(groupName, groupRules[0]);
                  }}
                  className="flex-1 px-2 py-1 text-xs rounded-md bg-khiimori-bg-elevated/10 border border-khiimori-border/10 text-khiimori-text-primary placeholder:text-khiimori-text-muted"
                />
                <button
                  type="button"
                  onClick={() => void addSiteToGroup(groupName, groupRules[0])}
                  className="px-2 py-1 text-xs rounded-md bg-khiimori-bg-elevated/20 text-khiimori-text-secondary hover:text-khiimori-text-primary"
                >
                  Add
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={showNewRule} onClose={() => setShowNewRule(false)} title="New Block Rule" size="sm">
        <div className="space-y-3">
          <Input
            label="Rule name"
            placeholder="e.g. Social Media"
            value={newRule.name}
            onChange={(e) => setNewRule((prev) => ({ ...prev, name: e.target.value }))}
          />
          <Input
            label="Domain"
            placeholder="e.g. tiktok.com"
            value={newRule.site}
            onChange={(e) => setNewRule((prev) => ({ ...prev, site: e.target.value }))}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setShowNewRule(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreateRule()}>Create</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
